<template>
    <div class="quiz-manager">
        <div class="quiz-manager-header">
            <h4><q-icon name="quiz" /> Gestion des quiz</h4>
            <q-btn icon="close" flat round dense v-close-popup />
        </div>

        <div class="quiz-manager-body">
            <!-- Colonne gauche : liste des quiz -->
            <div class="quiz-list">
                <div class="quiz-list-actions">
                    <q-input v-model="newQuizTitle" outlined dense label="Titre du nouveau quiz"
                        @keyup.enter="createQuiz" />
                    <q-btn unelevated dense no-caps color="secondary" icon="add" label="Créer"
                        :disable="!newQuizTitle.trim()" @click="createQuiz" />
                </div>
                <q-list separator>
                    <q-item v-for="q in quizzes" :key="q.id" clickable :active="selectedQuiz && selectedQuiz.id === q.id"
                        active-class="quiz-item-active" @click="selectQuiz(q.id)">
                        <q-item-section>{{ q.title }}</q-item-section>
                        <q-item-section side>
                            <div class="row items-center no-wrap">
                                <q-btn icon="insights" flat round dense color="secondary" @click.stop="openStats(q.id)">
                                    <q-tooltip>Statistiques</q-tooltip>
                                </q-btn>
                                <q-btn icon="delete" flat round dense color="negative" @click.stop="deleteQuiz(q)" />
                            </div>
                        </q-item-section>
                    </q-item>
                </q-list>
                <p v-if="!quizzes.length" class="quiz-empty">Aucun quiz pour cette exposition.</p>
            </div>

            <!-- Colonne droite : édition du quiz sélectionné -->
            <div class="quiz-editor" v-if="selectedQuiz">
                <q-input v-model="selectedQuiz.title" outlined dense label="Titre du quiz" class="q-mb-md" />

                <q-btn-toggle v-model="selectedQuiz.mode" dense outline no-caps toggle-color="secondary"
                    text-color="grey-8" class="q-mb-md" :options="[
                        { label: 'Quiz (scoré)', value: 'quiz' },
                        { label: 'Sondage', value: 'survey' }
                    ]" />

                <div class="appearance-card">
                    <div class="appearance-title"><q-icon name="palette" /> Apparence (fond et couleur des boutons)
                    </div>
                    <div class="appearance-row">
                        <q-input v-model="selectedQuiz.accent_color" outlined dense clearable
                            label="Couleur d'accent (boutons)" class="appearance-field">
                            <template v-slot:append>
                                <q-icon name="colorize" class="cursor-pointer">
                                    <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                                        <q-color v-model="selectedQuiz.accent_color" />
                                    </q-popup-proxy>
                                </q-icon>
                            </template>
                        </q-input>
                        <q-btn-toggle v-model="bgSource" dense outline no-caps toggle-color="secondary"
                            text-color="grey-8" :options="[
                                { label: 'Fond : couleur', value: 'color' },
                                { label: 'Fond : image', value: 'image' }
                            ]" />
                        <q-input v-if="bgSource === 'color'" v-model="selectedQuiz.bg_color" outlined dense clearable
                            label="Couleur de fond" class="appearance-field">
                            <template v-slot:append>
                                <q-icon name="colorize" class="cursor-pointer">
                                    <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                                        <q-color v-model="selectedQuiz.bg_color" />
                                    </q-popup-proxy>
                                </q-icon>
                            </template>
                        </q-input>
                        <q-select v-else v-model="selectedQuiz.bg_image_id" :options="resources" option-label="title"
                            option-value="id" emit-value map-options outlined dense clearable label="Image de fond"
                            class="appearance-field" />
                    </div>
                </div>

                <div v-for="(question, qi) in selectedQuiz.questions" :key="qi" class="question-card">
                    <div class="question-card-header">
                        <span class="question-number">Question {{ qi + 1 }}</span>
                        <q-btn-toggle v-model="question.type" dense outline no-caps toggle-color="secondary"
                            text-color="grey-8" :options="[
                                { label: 'Choix multiple', value: 'choice' },
                                { label: 'Vrai / Faux', value: 'truefalse' },
                                { label: 'Texte libre', value: 'open' }
                            ]" @update:model-value="onTypeChange(question)" />
                        <q-space />
                        <q-btn icon="arrow_upward" flat round dense :disable="qi === 0"
                            @click="moveQuestion(qi, -1)" />
                        <q-btn icon="arrow_downward" flat round dense
                            :disable="qi === selectedQuiz.questions.length - 1" @click="moveQuestion(qi, 1)" />
                        <q-btn icon="delete" flat round dense color="negative" @click="removeQuestion(qi)" />
                    </div>

                    <q-input v-model="question.text" outlined dense label="Texte de la question" class="q-mb-sm" />
                    <q-select v-model="question.resource_id" :options="resources" option-label="title"
                        option-value="id" emit-value map-options outlined dense clearable
                        label="Média (image ou audio, optionnel)" class="q-mb-sm" />

                    <template v-if="question.type !== 'open'">
                        <div v-for="(answer, ai) in question.answers" :key="ai" class="answer-row">
                            <q-radio v-if="selectedQuiz.mode !== 'survey'" :model-value="correctIndex(question)" :val="ai" dense
                                @update:model-value="setCorrect(question, ai)">
                                <q-tooltip>Bonne réponse</q-tooltip>
                            </q-radio>
                            <q-input v-model="answer.text" outlined dense class="answer-input"
                                :label="`Réponse ${ai + 1}`" :readonly="question.type === 'truefalse'" />
                            <q-btn v-if="question.type === 'choice'" icon="close" flat round dense
                                :disable="question.answers.length <= 2" @click="removeAnswer(question, ai)" />
                        </div>
                        <q-btn v-if="question.type === 'choice' && question.answers.length < 4" flat dense no-caps
                            color="secondary" icon="add" label="Ajouter une réponse" size="sm"
                            @click="addAnswer(question)" />
                    </template>
                    <p v-else class="open-hint">Réponse libre saisie par le visiteur (aucune option à définir).</p>
                </div>

                <div class="quiz-editor-actions">
                    <q-btn outline dense no-caps color="secondary" icon="add" label="Ajouter une question"
                        @click="addQuestion" />
                    <q-space />
                    <span v-if="savedMsg" class="saved-msg">{{ savedMsg }}</span>
                    <q-btn unelevated dense no-caps color="secondary" label="Enregistrer le quiz" :loading="saving"
                        :disable="!canSave" @click="saveQuiz" />
                </div>
            </div>
            <div class="quiz-editor quiz-empty" v-else>
                <p>Sélectionnez un quiz à gauche, ou créez-en un nouveau.</p>
            </div>
        </div>

        <q-dialog v-model="statsOpen" maximized>
            <QuizStats v-if="statsQuizId" :quizId="statsQuizId" />
        </q-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, onMounted } from 'vue';
import QuizStats from './QuizStats.vue';

export default {
    name: 'QuizManager',
    components: { QuizStats },
    props: { exhibitId: { required: true } },
    setup(props) {
        const quizzes = ref([]);
        const resources = ref([]);
        const selectedQuiz = ref(null);
        const newQuizTitle = ref('');
        const saving = ref(false);
        const savedMsg = ref('');
        const bgSource = ref('color');

        const fetchQuizzes = async () => {
            try {
                const url = `${process.env.VUE_APP_BASEURL}/quizzes/${props.exhibitId}`;
                quizzes.value = (await axios.get(url)).data;
            } catch (error) {
                console.log(error);
            }
        };

        const fetchResources = async () => {
            try {
                const url = `${process.env.VUE_APP_BASEURL}/resources/${props.exhibitId}`;
                resources.value = (await axios.get(url)).data;
            } catch (error) {
                console.log(error);
            }
        };

        const selectQuiz = async (id) => {
            try {
                const url = `${process.env.VUE_APP_BASEURL}/quiz/${id}`;
                selectedQuiz.value = (await axios.get(url)).data;
                if (!selectedQuiz.value.mode) selectedQuiz.value.mode = 'quiz';
                bgSource.value = selectedQuiz.value.bg_image_id ? 'image' : 'color';
                savedMsg.value = '';
            } catch (error) {
                console.log(error);
            }
        };

        const createQuiz = async () => {
            if (!newQuizTitle.value.trim()) return;
            try {
                const url = `${process.env.VUE_APP_BASEURL}/quiz`;
                const response = await axios.post(url, { exhibitId: props.exhibitId, title: newQuizTitle.value });
                newQuizTitle.value = '';
                await fetchQuizzes();
                await selectQuiz(response.data.id);
            } catch (error) {
                console.log(error);
            }
        };

        const deleteQuiz = async (q) => {
            if (!confirm(`Supprimer le quiz « ${q.title} » et toutes ses questions ?`)) return;
            try {
                await axios.delete(`${process.env.VUE_APP_BASEURL}/quiz/${q.id}`);
                if (selectedQuiz.value && selectedQuiz.value.id === q.id) selectedQuiz.value = null;
                await fetchQuizzes();
            } catch (error) {
                console.log(error);
            }
        };

        const addQuestion = () => {
            selectedQuiz.value.questions.push({
                type: 'choice',
                text: '',
                resource_id: null,
                answers: [
                    { text: '', is_correct: true },
                    { text: '', is_correct: false },
                ],
            });
        };

        const removeQuestion = (qi) => {
            selectedQuiz.value.questions.splice(qi, 1);
        };

        const moveQuestion = (qi, delta) => {
            const questions = selectedQuiz.value.questions;
            const [moved] = questions.splice(qi, 1);
            questions.splice(qi + delta, 0, moved);
        };

        const onTypeChange = (question) => {
            if (question.type === 'truefalse') {
                question.answers = [
                    { text: 'Vrai', is_correct: true },
                    { text: 'Faux', is_correct: false },
                ];
            } else if (question.type === 'open') {
                question.answers = [];
            } else {
                question.answers = [
                    { text: '', is_correct: true },
                    { text: '', is_correct: false },
                ];
            }
        };

        const addAnswer = (question) => {
            if (question.answers.length < 4) question.answers.push({ text: '', is_correct: false });
        };

        const removeAnswer = (question, ai) => {
            if (question.answers.length <= 2) return;
            const wasCorrect = question.answers[ai].is_correct;
            question.answers.splice(ai, 1);
            if (wasCorrect) question.answers[0].is_correct = true;
        };

        const correctIndex = (question) => question.answers.findIndex(a => a.is_correct);

        const setCorrect = (question, ai) => {
            question.answers.forEach((a, i) => { a.is_correct = i === ai; });
        };

        const canSave = computed(() => {
            const quiz = selectedQuiz.value;
            if (!quiz || !quiz.title.trim() || !quiz.questions.length) return false;
            const survey = quiz.mode === 'survey';
            return quiz.questions.every(q => {
                if (!q.text.trim()) return false;
                if (q.type === 'open') return true;
                if (q.answers.length < 2 || !q.answers.every(a => a.text.trim())) return false;
                return survey || q.answers.some(a => a.is_correct);
            });
        });

        const saveQuiz = async () => {
            saving.value = true;
            savedMsg.value = '';
            try {
                const quiz = selectedQuiz.value;
                const url = `${process.env.VUE_APP_BASEURL}/quiz/${quiz.id}`;
                await axios.put(url, {
                    title: quiz.title,
                    mode: quiz.mode || 'quiz',
                    accent_color: quiz.accent_color || null,
                    bg_color: bgSource.value === 'color' ? (quiz.bg_color || null) : null,
                    bg_image_id: bgSource.value === 'image' ? (quiz.bg_image_id || null) : null,
                    questions: quiz.questions.map(q => ({
                        id: q.id,
                        type: q.type,
                        text: q.text,
                        resource_id: q.resource_id,
                        answers: q.answers.map(a => ({ id: a.id, text: a.text, is_correct: a.is_correct })),
                    })),
                });
                savedMsg.value = 'Enregistré ✓';
                await fetchQuizzes();
            } catch (error) {
                console.log(error);
                savedMsg.value = "Erreur d'enregistrement";
            } finally {
                saving.value = false;
            }
        };

        // Stubs stats : le dialog + l'import QuizStats sont branchés dans une étape ultérieure.
        const statsOpen = ref(false);
        const statsQuizId = ref(null);
        const openStats = (id) => { statsQuizId.value = id; statsOpen.value = true; };

        onMounted(() => { fetchQuizzes(); fetchResources(); });

        return {
            quizzes, resources, selectedQuiz, newQuizTitle, saving, savedMsg, canSave, bgSource,
            selectQuiz, createQuiz, deleteQuiz, addQuestion, removeQuestion, moveQuestion,
            onTypeChange, addAnswer, removeAnswer, correctIndex, setCorrect, saveQuiz,
            statsOpen, statsQuizId, openStats,
        };
    },
};
</script>

<style scoped>
.quiz-manager {
    background: white;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.quiz-manager-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    border-bottom: 1px solid #e0e0e0;
}

.quiz-manager-header h4 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
}

.quiz-manager-body {
    flex: 1;
    display: flex;
    overflow: hidden;
}

.quiz-list {
    width: 300px;
    border-right: 1px solid #e0e0e0;
    padding: 12px;
    overflow-y: auto;
}

.quiz-list-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
}

.quiz-item-active {
    background: #e0f2f1;
    color: #000;
    border-left: 3px solid var(--q-secondary);
}

.quiz-editor {
    flex: 1;
    padding: 16px;
    overflow-y: auto;
}

.quiz-empty {
    color: #777;
    padding: 16px;
}

.appearance-card {
    border: 1px solid #eee;
    border-left: 3px solid var(--q-secondary);
    padding: 14px 16px;
    margin-bottom: 16px;
    background: #fbfbfb;
}

.appearance-title {
    font-weight: 600;
    color: #555;
    margin-bottom: 12px;
}

.appearance-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
}

.appearance-field {
    min-width: 220px;
}

.question-card {
    border: 1px solid #eee;
    padding: 16px;
    margin-bottom: 14px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.question-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.question-number {
    font-weight: 600;
}

.answer-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
}

.answer-input {
    flex: 1;
}

.quiz-editor-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    position: sticky;
    bottom: 0;
    background: white;
    padding: 12px 0;
    border-top: 1px solid #eee;
}

.saved-msg {
    color: #21ba45;
    font-weight: 600;
}

.open-hint {
    color: #777;
    font-style: italic;
    margin: 4px 0 0;
}
</style>
